import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { JJamesDropdownOption } from '../../../types/ui.types';
import { cn } from '../../../utils/class-utils';

/**
 * Search-select-or-create input: type to filter `options`, pick a match, or — when nothing matches
 * — commit the typed text as a new one via `(createNew)`. Purely presentational: this component
 * never calls an API itself. The parent owns persistence (calling whatever "create" command backs
 * the list) and feeds the result back through `options`/`value`.
 */
@Component({
    selector: 'jjames-autocomplete-create',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="relative">
      <input
        type="text"
        [class]="inputClasses"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [value]="displayValue"
        (input)="onInput($event)"
        (focus)="onFocus()"
        (keydown)="onKeydown($event)"
      />

      @if (loading) {
        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <div class="h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      }

      @if (isOpen && !disabled) {
        <div class="absolute z-10 mt-1 w-full max-h-64 overflow-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          @if (filteredOptions.length === 0 && !showCreateRow) {
            <div class="px-3 py-2 text-sm text-gray-500">No matches</div>
          }
          @for (option of filteredOptions; track option.value) {
            <button
              type="button"
              class="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 flex items-center justify-between"
              [class.bg-blue-50]="option.value === value"
              (mousedown)="$event.preventDefault(); selectOption(option)"
            >
              {{ option.label }}
            </button>
          }
          @if (showCreateRow) {
            <button
              type="button"
              class="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border-t border-gray-100 flex items-center gap-1.5"
              (mousedown)="$event.preventDefault(); confirmCreate()"
            >
              <span>Add "{{ searchTerm.trim() }}"</span>
            </button>
          }
        </div>
      }
    </div>
  `,
    styles: []
})
export class JJamesAutocompleteCreateComponent implements OnChanges {
    /** Full option list (already loaded by the parent) — filtering is client-side. */
    @Input() options: JJamesDropdownOption[] = [];
    /** Selected option's value, or null when nothing is selected. */
    @Input() value: string | null = null;
    @Input() placeholder = 'Search or type to add...';
    @Input() disabled = false;
    @Input() loading = false;
    /** When false the control is a plain search-select: no "Add ..." row and typed text is never committed. */
    @Input() allowCreate = true;

    /** Fires with an existing option's value when it is picked. */
    @Output() valueChange = new EventEmitter<string>();
    /**
     * Fires with the raw typed name when it doesn't case-insensitively match any option's label and
     * the user commits it (Enter, or the "Add ..." row). The parent persists it and is expected to
     * update `options`/`value` in response — this component does not add it to the list itself.
     */
    @Output() createNew = new EventEmitter<string>();

    isOpen = false;
    searchTerm = '';
    private editing = false;

    constructor(private elementRef: ElementRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.editing && (changes['value'] || changes['options'])) {
            this.searchTerm = this.selectedLabel;
        }
    }

    private get selectedLabel(): string {
        return this.options.find(o => o.value === this.value)?.label ?? '';
    }

    get displayValue(): string {
        return this.editing ? this.searchTerm : (this.searchTerm || this.selectedLabel);
    }

    get filteredOptions(): JJamesDropdownOption[] {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) return this.options;
        return this.options.filter(o => o.label.toLowerCase().includes(term));
    }

    private get exactMatch(): JJamesDropdownOption | undefined {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) return undefined;
        return this.options.find(o => o.label.toLowerCase() === term);
    }

    get showCreateRow(): boolean {
        return this.allowCreate && this.searchTerm.trim().length > 0 && !this.exactMatch;
    }

    get inputClasses(): string {
        const base = 'block w-full border rounded-md shadow-sm focus:ring-1 focus:outline-none transition-colors px-3 py-2 text-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500';
        const disabledClasses = this.disabled ? 'bg-gray-50 cursor-not-allowed' : '';
        return cn(base, disabledClasses);
    }

    onFocus(): void {
        if (this.disabled) return;
        this.editing = true;
        this.searchTerm = this.selectedLabel;
        this.isOpen = true;
    }

    onInput(event: Event): void {
        this.editing = true;
        this.searchTerm = (event.target as HTMLInputElement).value;
        this.isOpen = true;
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const match = this.exactMatch;
        if (match) {
            this.selectOption(match);
        } else if (this.showCreateRow) {
            this.confirmCreate();
        }
    }

    selectOption(option: JJamesDropdownOption): void {
        this.editing = false;
        this.searchTerm = option.label;
        this.isOpen = false;
        this.valueChange.emit(option.value);
    }

    confirmCreate(): void {
        const name = this.searchTerm.trim();
        if (!name) return;
        this.isOpen = false;
        this.editing = false;
        this.createNew.emit(name);
    }

    /**
     * Closing the field (click elsewhere, tabbing to the next field, submitting the form) must not
     * silently drop a typed name — "type a new word and it gets added" is the whole point of this
     * control. So a close with unmatched typed text commits it as a create, the same as pressing
     * Enter; only a close with no typed text (or an exact match already reflected in `value`) is a
     * plain "cancel this edit" that reverts the box to the current selection.
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        if (this.elementRef.nativeElement.contains(event.target)) return;
        if (!this.isOpen) return;

        if (this.showCreateRow) {
            this.confirmCreate();
        } else {
            this.isOpen = false;
            this.editing = false;
            this.searchTerm = this.selectedLabel;
        }
    }
}
